<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Awards from '$lib/PageSections/Awards/Awards.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import GradeTypes from '$lib/PageSections/GradeTypes/GradeTypes.svelte';
	import WhatDoTheyCost from '$lib/PageSections/WhatDoTheyCost/WhatDoTheyCost.svelte';

	import img1 from './images/solutions-quite-performance-001-1.jpg';
	import img2 from './images/solutions-quite-performance-002-1.jpg';
	import img3 from './images/solutions-quite-performance-003.jpg';
</script>

<Section backgroundColor="white">
	<p>
		Two independent studies have shown how effective Indow inserts are at
		soundproofing a window. All Indow window insert grades reduce outside noise
		by at least 50 percent when placed over single-pane windows. Acoustic Grade
		was specifically designed for soundproofing a window. It reduces outside
		sound by 70 percent when placed over single-pane windows and by 50 percent
		when placed over double-panes.*
	</p>
	<Image src={img1} alt="Indow window quiet performance family" />

	<Bubble>
		{#snippet titleSlot()}
			<div class="mb-4 flex flex-col gap-4">
				<a
					class="text-center"
					href="/static/pdfs/Indow_report_March_2013_FINAL.pdf"
					><h2 class="text-base font-normal">
						Portland State University’s Green Building Research Laboratory Study
					</h2>
				</a>
				<h3>Summary of results:</h3>
			</div>
		{/snippet}
		<ul>
			<li>
				Indow Acoustic Grade inserts reduce noise by 18.9 dBA when placed over
				single-pane windows.
			</li>
			<li>
				Indow Standard Grade inserts reduce noise by 10-12 dBA when placed over
				single-pane windows.
			</li>
		</ul>
	</Bubble>
</Section>

<Section backgroundColor="white">
	<h2>What was involved in the testing to block sound from windows?</h2>
	<p>
		Green Building Research Lab director David Sailor <a
			href="https://www.opb.org/news/article/green-buildings"
			>spearheaded the study</a
		>. His lab researches indoor and urban air quality, air pollution and
		sustainable buildings.
	</p>
	<p>
		Indow Standard Grade inserts (⅛” thick) were placed over mock single-pane
		windows inside a 6’ X 4’ plywood enclosure. White noise was generated inside
		the box and decibel reduction was compared with and without the insert.
		Researchers used an Omega HHSL1 sound level meter to measure inside and
		outside levels.
	</p>
	<Image src={img2} alt="Indow window quiet performance testing." />
	<p>
		Researchers measured the Outdoor-Indoor Level Reduction (OILR) of the
		single-pane window, then the single-pane + the insert. The difference
		between the two was the reduction in noise the insert provided to block
		sound from windows.
	</p>
	<p>
		This study proved the inserts reduce outside noise and that the thicker,
		Acoustic Grade, did a superior job. If your primary concern is blocking
		sound from your window, Acoustic Grade is your best choice.
	</p>
	<hr />

	<Bubble>
		{#snippet titleSlot()}
			<div class="mb-4 flex flex-col gap-4">
				<a
					href="/static/pdfs/Indow_Field-Sound-Transmission-Loss_081116.pdf"
					class="text-center">British Columbia Institute of Technology</a
				>
				<h3>Summary of results:</h3>
			</div>
		{/snippet}
		<ul>
			<li>
				Increased the Sound Transmission Class (STC) to 42-45 with inserts over
				double-pane windows, which is roughly equal to an insulated wall.
			</li>
			<li>
				Average sound insulation improvement of 14dB across frequency bands
				between 125 and 4k Hz.
			</li>
		</ul>
	</Bubble>
</Section>

<Section backgroundColor="white">
	<h2>What was involved in the testing to block sound from windows?</h2>
	<p>
		The Centre for Architectural Ecology/Building Science Graduate Program at
		the British Columbia Institute of Technology conducted the study. A
		dedicated facility for applied research and education, it conducts
		third-party testing of industry products.
	</p>
	<p>
		Program researchers set up an omnidirectional speaker in a testing facility.
		Sound intensity measurements were taken with a SINUS Soundbook noise
		monitoring system, using Samurai software, and a SINUS Type 1 intensity
		probe. They took baseline tests of the double-pane windows and then tests
		with the inserts installed at 3” and 6” away from the window glass.
	</p>
	<hr />
</Section>

<Section backgroundColor="white">
	<h2>STC Ratings & In-Home Results</h2>
	<p>
		Many factors affect how well window inserts reduce noise. You may experience
		a dramatic reduction in one room while in another it’s less noticeable. Our <a
			href="/resources/home-soundproofing">Soundproofing Your Home</a
		>
		articles give an overview of how sound enters a space to help inform your overall
		noise reduction strategy.
	</p>
	<p>
		The diagram below shows where Indow inserts fall in the <a
			href="/resources/stc-rating">STC rating</a
		>
		(Sound Transmission Class). With STC rating, the higher the score, the less noise
		can penetrate. An insulated wall as a much higher STC rating than a single-pane
		window. As you can see in the diagram, Indow inserts combined with a single-pane
		window block noise better than an insulated wall. Read the
		<a href="/resources/faq/interior-storm-window-inserts-glossary#acoustic"
			>definition of STC rating</a
		>.
	</p>
	<Image src={img3} alt="Indow window quiet insert." />
	<p>
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
	<p>
		Your sales representative can also help by asking questions about your home,
		as well as the noise affecting you, to determine whether Indow is a good
		solution for you. Fill out our form for a free consultation.
	</p>
</Section>

<WhatDoTheyCost backgroundColor="blue" />
<GradeTypes backgroundColor="grey-light" />
<GetAFreeEstimate />
<Awards />
